// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, OnInit} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { IIntegration, PartnerTypes } from 'src/types/integrations';
import { ActivatedRoute, Router } from '@angular/router';
import { D3eService } from 'src/services/d3e-service';
import { HeaderService } from 'src/app/header.service';
import { NotificationService } from 'src/services/notification-service';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import {SelectedOrgMethod} from "../../../../shared/selectedOrg";
import {TranslateService} from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WindowService } from '../../../../services/window.service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-cisco-amp',
    templateUrl: './cisco-amp.component.html',
    styleUrls: ['./cisco-amp.component.scss'],
    standalone: false
})
export class CiscoAmpComponent implements OnInit {

  private readonly PARTNER_ID = PartnerTypes.CISCO_AMP;
  formGroup: UntypedFormGroup;
  integration: IIntegration;
  integrationId: string = null;
  loading = true;

  selectedOrgId: string;
  hasUserBeenSet = false;

  fAPI: string;
  fAPIPHolder: string;
  fID: string;
  fIDPHolder: string;
  fEndpoint: string;
  fEndpointPHolder: string;
  fLockA: string;
  fLockB: string;
  fLockC: string;
  fAdmin: string;
  fCancel: string;
  fSave: string;
  fYes: string;
  fNo: string;
  fDeleteTitle: string;
  fDeleteContent: string;
  fConfigDeleted: string;
  fFailedToDelete: string;
  fDiscardChanges: string;
  fDiscardChangesContent: string;
  fConfigurationSaved: string;
  fReportSaveError: string;
  fChangesBeingSaved: string;
  
  title: string;
  subtitle: string;
  constructor(
    private route: ActivatedRoute,
    private d3eService: D3eService,
    private router: Router,
    private headerService: HeaderService,
    private formBuilder: UntypedFormBuilder,
    private notification: NotificationService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private windowService: WindowService
  ) {
    this.translate.get(['INTC_LOCKA', 'INTC_LOCKB', 'INTC_LOCKC', 'INTC_ADMIN', 'CANCEL_CAP',
      'SAVE_CAP', 'INTCA_API', 'INTCA_API_PHOLDER', 'INTCA_ID', 'INTCA_ID_PHOLDER', 'INTCA_ENDPOINT',
      'INTCA_ENDPOINT_PHOLDER', 'TITLE_CISCOA_INT', 'SUBTITLE_CISCOA', 'YES', 'NO', 'INT_DELETE_CONFIRM_TITLE',
      'INT_DELETE_CONFIRM_TEXT', 'CONFIGURATION_DELETED', 'INT_FAILED_TO_DELETE', 'DISCARD_CHANGES_CONTENT',
      'DISCARD_CHANGES', 'CONFIGURATION_SAVED', 'REPORT_SAVE_ERROR', 'CHANGES_BEING_SAVED']).subscribe(vals => {
        this.fAPI = vals ['INTCA_API'];
        this.fAPIPHolder = vals ['INTCA_API_PHOLDER'];
        this.fID = vals ['INTCA_ID'];
        this.fIDPHolder = vals ['INTCA_ID_PHOLDER'];
        this.fEndpoint = vals ['INTCA_ENDPOINT'];
        this.fEndpointPHolder = vals ['INTCA_ENDPOINT_PHOLDER'];
        this.fLockA = vals ['INTC_LOCKA'];
        this.fLockB = vals ['INTC_LOCKB'];
        this.fLockC = vals ['INTC_LOCKC'];
        this.fAdmin = vals ['INTC_ADMIN'];
        this.fCancel = vals ['CANCEL_CAP'];
        this.fSave = vals ['SAVE_CAP'];
        this.fYes = vals['YES'];
        this.fNo = vals['NO'];
        this.fDeleteTitle = vals['INT_DELETE_CONFIRM_TITLE'];
        this.fDeleteContent = vals['INT_DELETE_CONFIRM_TEXT'];
        this.fConfigDeleted = vals['CONFIGURATION_DELETED'];
        this.fFailedToDelete = vals['INT_FAILED_TO_DELETE'];
        this.fDiscardChanges = vals['DISCARD_CHANGES'];
        this.fDiscardChangesContent = vals['DISCARD_CHANGES_CONTENT'];
        this.fConfigurationSaved = vals['CONFIGURATION_SAVED'];
        this.fReportSaveError = vals['REPORT_SAVE_ERROR'];
        this.fChangesBeingSaved = vals['CHANGES_BEING_SAVED'];

        this.title = vals['TITLE_CISCOA_INT'];
        this.subtitle = vals['SUBTITLE_CISCOA'];
    });
    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle(this.subtitle);
    this.headerService.setIcon('mdi-sync');

    this.formGroup = this.formBuilder.group({
      client_id: ['', Validators.compose([Validators.required,
          Validators.pattern(/^[0-9a-zA-Z]/)])],
      api_key: ['', Validators.required],
      api_host: ['api.amp.cisco.com', Validators.required],
      lock_option: ['', Validators.required],
      admin_unlock: [{value: false, disabled: true}],

    });
    this.formGroup.controls['api_host'].setValue('api.amp.cisco.com');
  }

  ngOnInit() {
    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
            // TODO: launch the dirty flag window
            this.router.navigateByUrl('/integrations');
        }
        this.hasUserBeenSet = true;
    });

    this.route.params.subscribe(params => {
      this.integrationId = params.id;
      if (this.integrationId === 'new') {
        this.loading = false;
      } else {
        const selectedUser = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.get_integration(this.integrationId, selectedUser)
          .pipe(untilDestroyed(this))
          .subscribe(integration => {
            if (integration.partner_id !== this.PARTNER_ID) {
              this.router.navigateByUrl('/integrations');
            }
            this.formGroup.controls['api_key'].setValue(integration.config.api_key);
            this.formGroup.controls['client_id'].setValue(integration.config.client_id);
            this.formGroup.controls['api_host'].setValue(integration.config.amp_api_host);
            this.formGroup.controls['lock_option'].setValue(integration.config.lock_option);
            this.formGroup.controls['admin_unlock'].setValue(integration.config.admin_unlock);

            this.integration = integration;
        });
      }
    });
  }

  save() {
    this.notification.showUpdate(this.fChangesBeingSaved);
    const data = this.formGroup.value;
    if (this.integrationId === 'new') {
      const integration: IIntegration = {
        display_name: 'Cisco Secure Endpoint',
        enabled: true,
        partner_id: PartnerTypes.CISCO_AMP,
        config: {
          client_id: data.client_id,
          api_key: data.api_key,
          amp_api_host: data.api_host,
          unique_id: this.windowService.cryptoRandom().toString(36).substring(2, 8) +
            this.windowService.cryptoRandom().toString(36).substring(2, 8),
          admin_unlock: data.admin_unlock || false,
          lock_option: data.lock_option,
        }
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.add_integration(integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    } else {
      this.integration.display_name = 'Cisco Secure Endpoint';
      this.integration.config = {
        client_id: data.client_id,
        api_key: data.api_key,
        amp_api_host: data.api_host,
        unique_id: this.integration.config.unique_id,
        admin_unlock: data.admin_unlock || false,
        lock_option: data.lock_option,
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.update_integration(this.integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    }
  }

  reportSaveSuccess() {
    this.notification.showSuccess(this.fConfigurationSaved);
  }

  reportSaveError() {
    this.notification.showError(this.fReportSaveError);
  }

  goToIntegrations() {
    this.router.navigateByUrl('/integrations');
  }

  cancel() {
    if (this.formGroup.touched) {
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: this.fDiscardChanges,
          content: this.fDiscardChangesContent,
          rejectBtnName: this.fNo,
          acceptBtnName: this.fYes,
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.goToIntegrations();
        }
      });
    } else {
      this.goToIntegrations();
    }
  }

  remove() {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title: this.fDeleteTitle,
        content: this.fDeleteContent,
        rejectBtnName: this.fNo,
        acceptBtnName: this.fYes,
        showRejectBtn: true,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.delete_integration(this.integration.id, selectedOrg)
          .subscribe(() => {
            this.reportDeleteSuccess();
            this.goToIntegrations();
          }, this.reportDeleteError.bind(this));
      }
    });
  }

  reportDeleteSuccess() {
    this.notification.showSuccess(this.fConfigDeleted);
  }

  reportDeleteError() {
    this.notification.showError(this.fFailedToDelete);
  }
}
